'use client';

import { useTranslation } from 'react-i18next';
import {
  useCurrentUser,
  useMicroSource,
  type MicroContentItem,
  type MicroContentKind,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { ContentRail } from '../ui/ContentRail';
import { MicroItemCard } from './_MicroItemCard';
import { microCardGridStyle } from './_cardGrid';
import { useMicroCardDecorations } from './_useMicroCardDecorations';

export interface MicroContentRailProps {
  title?: string;
  subtitle?: string;
  /** {@code in-progress} = what the signed-in learner started and hasn't
   *  finished, most recent first; renders nothing for a visitor. */
  source?: 'featured' | 'popular' | 'latest' | 'in-progress';
  kind?: MicroContentKind;
  limit?: number;
  layout?: 'rail' | 'grid';
  showHeading?: boolean;
  seeAllHref?: string;
  itemHrefTemplate?: string;
  /** Show the content-kind chip over each card's cover. */
  showKindBadge?: boolean;
  /** Narrow the {@code latest} feed to items linked to this course. Set by
   *  course-page compositions, not an operator prop — a generic page has
   *  no course to bind to. */
  courseId?: string;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function MicroContentRail({
  title,
  subtitle,
  // Default matches the seed schema's `latest`.
  source = 'latest',
  kind,
  limit: rawLimit,
  layout = 'rail',
  showHeading = true,
  seeAllHref,
  itemHrefTemplate,
  showKindBadge = true,
  courseId,
}: MicroContentRailProps) {
  const { t } = useTranslation('site-builder-blocks');
  const micro = useMicroSource();
  const user = useCurrentUser();
  const decorations = useMicroCardDecorations();
  const limit = clamp(
    typeof rawLimit === 'number' && Number.isFinite(rawLimit) ? rawLimit : 8,
    1,
    24,
  );

  const state = useAsyncResource<MicroContentItem[]>(
    async (signal) => {
      if (!micro) return null;
      let items: MicroContentItem[];
      if (source === 'featured') items = await micro.content.featured({ limit, signal });
      else if (source === 'popular') items = await micro.content.popular({ limit, signal });
      else if (source === 'in-progress') {
        // The learner's own unfinished items: progress rows first, then
        // the items behind them (bounded by `limit`, so at most a handful
        // of detail reads). Visitors have no rows — the rail stays hidden.
        // fetchMine is optional on the progress namespace — a source that
        // can't list the learner's rows simply has no rail to show.
        const fetchMine = micro.progress?.fetchMine?.bind(micro.progress);
        if (!user || !fetchMine) return null;
        const rows = (await fetchMine(signal))
          .filter((r) => r.status === 'in-progress')
          .sort((a, b) => (b.lastSeenAt ?? '').localeCompare(a.lastSeenAt ?? ''))
          .slice(0, limit);
        const fetched = await Promise.all(
          rows.map((r) => micro.content.fetchById(r.itemId, signal).catch(() => null)),
        );
        items = fetched.filter((it): it is MicroContentItem => !!it && (!kind || it.kind === kind));
      } else {
        const res = await micro.content.list({
          kind,
          course: courseId,
          page: 1,
          pageSize: limit,
          // 'latest' is a real sort, not just a label on the seeded rail.
          sort: source === 'latest' ? 'latest' : undefined,
          signal,
        });
        items = res.items;
      }
      return items.length > 0 ? items : null;
    },
    [micro, source, kind, limit, user, courseId],
  );

  const action = seeAllHref ? (
    <a
      href={seeAllHref}
      style={{
        fontSize: '13px',
        fontWeight: 600,
        color: 'var(--color-text-brand, var(--color-brand-600, #3b5bdb))',
        textDecoration: 'none',
      }}
    >
      {t('microContentRail.seeAll')}
    </a>
  ) : undefined;

  if (!micro) {
    return (
      <SectionShell
        title={showHeading ? title : undefined}
        subtitle={showHeading ? subtitle : undefined}
        action={action}
      >
        <AdapterPlaceholder blockType="MicroContentRail" adapterName="MicroSource" />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell
        title={showHeading ? title : undefined}
        subtitle={showHeading ? subtitle : undefined}
        action={action}
      >
        <SkeletonRow count={limit} layout={layout} />
      </SectionShell>
    );
  }

  if (state.kind !== 'loaded') return null;

  if (layout === 'grid') {
    return (
      <SectionShell
        title={showHeading ? title : undefined}
        subtitle={showHeading ? subtitle : undefined}
        action={action}
      >
        <div style={microCardGridStyle(kind)}>
          {state.data.map((item) => (
            <MicroItemCard
              showKindBadge={showKindBadge}
              cover={kind === 'SHORTS' ? 'portrait' : 'landscape'}
              key={item.id}
              item={item}
              progress={decorations.progressFor(item.id)}
              categoryName={decorations.categoryNameFor(item.categoryId)}
              {...(itemHrefTemplate ? { hrefTemplate: itemHrefTemplate } : {})}
            />
          ))}
        </div>
      </SectionShell>
    );
  }

  return (
    <SectionShell
      title={showHeading ? title : undefined}
      subtitle={showHeading ? subtitle : undefined}
      action={action}
    >
      <ContentRail
        ariaLabel={title || t('microContentRail.ariaLabel')}
        // Shorts rails ride the narrower legacy width so the 9:16
        // portrait cards don't tower over the row.
        itemWidth={kind === 'SHORTS' ? '175px' : '240px'}
        snap="start"
      >
        {state.data.map((item) => (
          <MicroItemCard
            showKindBadge={showKindBadge}
            cover={kind === 'SHORTS' ? 'portrait' : 'landscape'}
            key={item.id}
            item={item}
            progress={decorations.progressFor(item.id)}
            categoryName={decorations.categoryNameFor(item.categoryId)}
            {...(itemHrefTemplate ? { hrefTemplate: itemHrefTemplate } : {})}
          />
        ))}
      </ContentRail>
    </SectionShell>
  );
}
